import React, { useState, useEffect } from 'react';
import segmentService from '../../services/segmentService';

interface Segment {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  never_contact: boolean;
}

interface Lead {
  id: number;
  email: string;
  parent_name?: string;
  segment_tags: string[];
}

interface SegmentManagementModalProps {
  lead: Lead;
  segments: Segment[];
  onClose: () => void;
  onUpdate: () => void;
}

const SegmentManagementModal: React.FC<SegmentManagementModalProps> = ({
  lead,
  segments,
  onClose,
  onUpdate
}) => {
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);
  const [originalSegmentIds, setOriginalSegmentIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Find which segment IDs this lead currently has by matching segment names
    const currentSegmentIds = segments
      .filter(segment => lead.segment_tags?.includes(segment.name))
      .map(segment => segment.id);

    setSelectedSegmentIds(currentSegmentIds);
    setOriginalSegmentIds(currentSegmentIds);
  }, [lead, segments]);

  const toggleSegment = (segmentId: number) => {
    setSelectedSegmentIds(prev =>
      prev.includes(segmentId)
        ? prev.filter(id => id !== segmentId)
        : [...prev, segmentId]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // Determine which segments to add and which to remove
      const segmentsToAdd = selectedSegmentIds.filter(
        id => !originalSegmentIds.includes(id)
      );
      const segmentsToRemove = originalSegmentIds.filter(
        id => !selectedSegmentIds.includes(id)
      );

      // Execute API calls for additions
      for (const segmentId of segmentsToAdd) {
        await segmentService.assignLeadsToSegment(segmentId, [lead.id]);
      }

      // Execute API calls for removals
      for (const segmentId of segmentsToRemove) {
        await segmentService.removeLeadsFromSegment(segmentId, [lead.id]);
      }

      // Success - refresh the leads list and close modal
      onUpdate();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update segment assignments');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = JSON.stringify([...selectedSegmentIds].sort()) !== JSON.stringify([...originalSegmentIds].sort());

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="px-6 py-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Manage Segments
              </h2>
              <p className="text-sm text-gray-500">
                {lead.parent_name || lead.email}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors duration-200"
              disabled={isSaving}
            >
              ×
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Segment Selection */}
          <div className="space-y-2 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select segments for this lead:
            </label>

            {segments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No segments available.</p>
                <p className="text-sm mt-2">Create segments first to assign leads.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {segments.map(segment => (
                  <label
                    key={segment.id}
                    className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedSegmentIds.includes(segment.id)
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSegmentIds.includes(segment.id)}
                      onChange={() => toggleSegment(segment.id)}
                      disabled={isSaving}
                      className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">
                          {segment.name}
                        </span>
                        {selectedSegmentIds.includes(segment.id) && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            Selected
                          </span>
                        )}
                      </div>
                      {segment.description && (
                        <p className="text-xs text-gray-500 mt-1">
                          {segment.description}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <span className="inline-block animate-spin mr-2">⟳</span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentManagementModal;
